import type { SuccessEnvelope } from "@whitehaven/contracts";
import {
  isPortalCapabilityAction,
  isPortalCapabilityArea,
  isPortalCapabilityPermission,
  isRole,
  portalCapabilityActions,
  portalCapabilityAreas,
  roles,
  type PortalCapabilityActionDescriptor,
  type PortalCapabilityAreaDescriptor,
  type PortalCapabilityPermission,
  type PortalRoleAccessRole,
  type PortalRoleAccessSnapshot,
  type ReplacePortalRoleAccessResponse,
  type Role,
} from "@whitehaven/permissions";

export type RoleAccessFetch = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

export class RoleAccessApiError extends Error {
  readonly status: number;
  readonly codes: readonly string[];
  readonly correlationId: string | null;

  constructor(
    message: string,
    status: number,
    codes: readonly string[] = [],
    correlationId: string | null = null,
  ) {
    super(message);
    this.name = "RoleAccessApiError";
    this.status = status;
    this.codes = codes;
    this.correlationId = correlationId;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }
}

export interface RoleAccessApiClient {
  readonly apiUrl: string;
  load(signal?: AbortSignal): Promise<PortalRoleAccessSnapshot>;
  replace(
    role: Role,
    csrfToken: string,
    permissions: readonly PortalCapabilityPermission[],
    expectedRevision: number,
  ): Promise<ReplacePortalRoleAccessResponse>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

async function responseJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    throw new RoleAccessApiError(
      "The access service returned an unreadable response.",
      response.status,
    );
  }
}

function apiFailure(response: Response, body: unknown) {
  const envelope = isRecord(body) && body.ok === false ? body : null;
  const errors =
    envelope && Array.isArray(envelope.errors)
      ? envelope.errors.filter(isRecord)
      : [];
  const message =
    errors.find((error) => typeof error.message === "string")?.message ??
    "The role access request could not be completed.";
  const codes = errors
    .map((error) => error.code)
    .filter((code): code is string => typeof code === "string");
  const correlationId =
    envelope && typeof envelope.correlationId === "string"
      ? envelope.correlationId
      : response.headers.get("x-correlation-id");

  return new RoleAccessApiError(
    message as string,
    response.status,
    codes,
    correlationId,
  );
}

async function envelopeData(response: Response): Promise<unknown> {
  const body = await responseJson(response);
  if (!response.ok) throw apiFailure(response, body);
  if (!isRecord(body) || body.ok !== true || !("data" in body)) {
    throw new RoleAccessApiError(
      "The access service returned an invalid response envelope.",
      response.status,
    );
  }
  return (body as unknown as SuccessEnvelope<unknown>).data;
}

function parseAction(value: unknown): PortalCapabilityActionDescriptor {
  if (
    !isRecord(value) ||
    typeof value.key !== "string" ||
    !isPortalCapabilityAction(value.key) ||
    typeof value.label !== "string" ||
    value.label.trim() === ""
  ) {
    throw new RoleAccessApiError(
      "The access service returned an invalid capability action.",
      502,
    );
  }
  return { key: value.key, label: value.label };
}

function parseArea(value: unknown): PortalCapabilityAreaDescriptor {
  if (
    !isRecord(value) ||
    typeof value.key !== "string" ||
    !isPortalCapabilityArea(value.key) ||
    typeof value.label !== "string" ||
    value.label.trim() === "" ||
    !["Guest", "Staff", "Administration"].includes(
      typeof value.section === "string" ? value.section : "",
    ) ||
    !Array.isArray(value.actions)
  ) {
    throw new RoleAccessApiError(
      "The access service returned an invalid capability area.",
      502,
    );
  }

  const actions = value.actions.map(parseAction);
  const actionKeys = new Set(actions.map(({ key }) => key));
  if (
    actions.length !== portalCapabilityActions.length ||
    portalCapabilityActions.some((action) => !actionKeys.has(action))
  ) {
    throw new RoleAccessApiError(
      "The access service returned an incomplete capability catalog.",
      502,
    );
  }

  return {
    key: value.key,
    label: value.label,
    section: value.section as PortalCapabilityAreaDescriptor["section"],
    actions,
  };
}

function parseRole(value: unknown): PortalRoleAccessRole {
  if (
    !isRecord(value) ||
    typeof value.key !== "string" ||
    !isRole(value.key) ||
    typeof value.label !== "string" ||
    value.label.trim() === "" ||
    typeof value.isActive !== "boolean" ||
    !Array.isArray(value.permissions) ||
    !value.permissions.every(
      (permission): permission is PortalCapabilityPermission =>
        typeof permission === "string" &&
        isPortalCapabilityPermission(permission),
    ) ||
    !Number.isSafeInteger(value.revision) ||
    (value.revision as number) < 0
  ) {
    throw new RoleAccessApiError(
      "The access service returned an invalid role grant set.",
      502,
    );
  }

  return {
    key: value.key,
    label: value.label,
    isActive: value.isActive,
    permissions: [...new Set(value.permissions)].sort(),
    revision: value.revision as number,
  };
}

export function parseRoleAccessSnapshot(
  value: unknown,
): PortalRoleAccessSnapshot {
  if (
    !isRecord(value) ||
    value.schemaVersion !== 1 ||
    typeof value.organizationId !== "string" ||
    value.organizationId.trim() === "" ||
    typeof value.actorRole !== "string" ||
    !isRole(value.actorRole) ||
    !Array.isArray(value.roles) ||
    !Array.isArray(value.areas)
  ) {
    throw new RoleAccessApiError(
      "The access service returned an invalid role access snapshot.",
      502,
    );
  }

  const parsedRoles = value.roles.map(parseRole);
  const parsedAreas = value.areas.map(parseArea);
  const roleKeys = new Set(parsedRoles.map(({ key }) => key));
  const areaKeys = new Set(parsedAreas.map(({ key }) => key));
  if (
    parsedRoles.length !== roles.length ||
    roleKeys.size !== roles.length ||
    roles.some((role) => !roleKeys.has(role)) ||
    parsedAreas.length !== portalCapabilityAreas.length ||
    areaKeys.size !== portalCapabilityAreas.length ||
    portalCapabilityAreas.some((area) => !areaKeys.has(area))
  ) {
    throw new RoleAccessApiError(
      "The access service returned an incomplete role access catalog.",
      502,
    );
  }

  return {
    schemaVersion: 1,
    organizationId: value.organizationId,
    actorRole: value.actorRole,
    roles: parsedRoles,
    areas: parsedAreas,
  };
}

function parseReplaceResponse(value: unknown): ReplacePortalRoleAccessResponse {
  if (
    !isRecord(value) ||
    typeof value.actorSessionRefreshRequired !== "boolean"
  ) {
    throw new RoleAccessApiError(
      "The access service returned an invalid saved role response.",
      502,
    );
  }
  return {
    role: parseRole(value.role),
    actorSessionRefreshRequired: value.actorSessionRefreshRequired,
  };
}

export function createRoleAccessApiClient(
  apiUrl: string,
  fetcher: RoleAccessFetch = fetch,
): RoleAccessApiClient {
  const normalizedApiUrl = apiUrl.replace(/\/+$/u, "");
  const endpoint = (path: string) => `${normalizedApiUrl}${path}`;

  return {
    apiUrl: normalizedApiUrl,
    async load(signal) {
      const response = await fetcher(endpoint("/v1/admin/roles/access"), {
        credentials: "include",
        headers: { Accept: "application/json" },
        ...(signal ? { signal } : {}),
      });
      return parseRoleAccessSnapshot(await envelopeData(response));
    },
    async replace(role, csrfToken, permissions, expectedRevision) {
      if (csrfToken.trim() === "") {
        throw new RoleAccessApiError(
          "Refresh your secure session before saving role access.",
          401,
          ["CSRF_REQUIRED"],
        );
      }
      if (
        !Number.isSafeInteger(expectedRevision) ||
        expectedRevision < 0 ||
        permissions.some(
          (permission) => !isPortalCapabilityPermission(permission),
        )
      ) {
        throw new RoleAccessApiError(
          "The role access change is invalid.",
          400,
          ["INVALID_ROLE_ACCESS"],
        );
      }

      const response = await fetcher(
        endpoint(`/v1/admin/roles/${encodeURIComponent(role)}/access`),
        {
          method: "PUT",
          credentials: "include",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            permissions: [...new Set(permissions)].sort(),
            expectedRevision,
          }),
        },
      );
      return parseReplaceResponse(await envelopeData(response));
    },
  };
}
